import React, { useEffect, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkIcon from '@mui/icons-material/Link';
import MicOffIcon from '@mui/icons-material/MicOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import TuneIcon from '@mui/icons-material/Tune';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';

import {
  clearAllStorage,
  clearAudioCache,
  clearEncounterState,
  clearIntegrations,
  clearPreferencesAndUI,
  clearRecordingState,
  clearReduxPersist,
  isRecordingActive,
} from '../../utils/storageManagement';

import { StorageClearConfirmDialog } from './StorageClearConfirmDialog';

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontWeight: 600,
  color: theme.palette.primary.main,
}));

type ClearAction =
  | 'clearEncounter'
  | 'clearRecording'
  | 'clearAudio'
  | 'clearAppState'
  | 'clearUIPreferences'
  | 'clearIntegrations'
  | 'clearAll'
  | null;

export interface StorageManagementCardProps {
  /**
   * Optional callback when storage is cleared
   */
  onStorageCleared?: (action: string) => void;
}

export const StorageManagementCard: React.FC<StorageManagementCardProps> = ({
  onStorageCleared,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [recordingActive, setRecordingActive] = useState<boolean>(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState<ClearAction>(null);
  const [isClearing, setIsClearing] = useState(false);

  // Load storage info on mount
  useEffect(() => {
    const loadStorageInfo = () => {
      try {
        setRecordingActive(isRecordingActive());
      } catch (error) {
        console.error('Failed to load storage info:', error);
      }
    };

    loadStorageInfo();

    // Poll recording status every 5 seconds
    const interval = setInterval(() => {
      setRecordingActive(isRecordingActive());
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Get dialog props based on action
  const getConfirmDialogProps = (action: ClearAction) => {
    switch (action) {
      case 'clearEncounter':
        return {
          title: 'Clear Encounter State?',
          description:
            'This will reset the active encounter. You may need to select your patient again.',
          itemsToClear: ['Active encounter ID'],
          itemsToKeep: ['Login session', 'User preferences', 'App settings'],
          requiresReload: false,
          requiresReauth: false,
        };
      case 'clearRecording':
        return {
          title: 'Clear Recording State?',
          description:
            'This will reset all recording-related state. Any in-progress recording will be lost.',
          itemsToClear: [
            'Recording status',
            'Recording session',
            'Elapsed time',
            'Microphone settings',
          ],
          itemsToKeep: ['Login session', 'User preferences', 'Completed recordings'],
          requiresReload: false,
          requiresReauth: false,
        };
      case 'clearAudio':
        return {
          title: 'Clear Audio Cache?',
          description:
            'This will delete cached audio recordings and session metadata from IndexedDB to free up space.',
          itemsToClear: [
            'Audio recordings (SmartScribeAudioChunks)',
            'Recording sessions (SmartScribeRecordingSessions)',
          ],
          itemsToKeep: ['Login session', 'User preferences', 'App state', 'Submitted notes'],
          requiresReload: false,
          requiresReauth: false,
        };
      case 'clearAppState':
        return {
          title: 'Reset App State?',
          description: 'This will reset the app to a fresh state while keeping you logged in.',
          itemsToClear: ['App state', 'Redux store', 'Cached data'],
          itemsToKeep: ['Login session', 'User account'],
          requiresReload: true,
          requiresReauth: false,
        };
      case 'clearUIPreferences':
        return {
          title: 'Reset UI Preferences?',
          description:
            'This will reset all UI settings to defaults. Your data and login will be preserved.',
          itemsToClear: [
            'Note filters and sorting',
            'Sidebar settings',
            'Tooltips and badges',
            'Onboarding flags',
            'Panel sizes',
          ],
          itemsToKeep: ['Login session', 'User data', 'Recordings', 'Encounters'],
          requiresReload: true,
          requiresReauth: false,
        };
      case 'clearIntegrations':
        return {
          title: 'Reset Integrations?',
          description:
            'This will clear OAuth and integration state. You may need to reconnect third-party services.',
          itemsToClear: ['Google OAuth state', 'Module customization workflows', 'Integration tokens'],
          itemsToKeep: ['Login session', 'User data', 'App preferences'],
          requiresReload: true,
          requiresReauth: false,
        };
      case 'clearAll':
        return {
          title: 'Clear All Storage?',
          description: 'This will clear everything and log you out. Use this as a last resort.',
          itemsToClear: [
            'Login session',
            'App state',
            'Cached data',
            'Audio recordings',
            'All settings',
          ],
          itemsToKeep: [],
          requiresReload: true,
          requiresReauth: true,
        };
      default:
        return {
          title: 'Confirm Action',
          description: '',
          itemsToClear: [],
          itemsToKeep: [],
        };
    }
  };

  const handleClearAction = (action: ClearAction) => {
    setCurrentAction(action);
    setConfirmDialogOpen(true);
  };

  const handleConfirmClear = async () => {
    if (!currentAction) return;

    setIsClearing(true);

    try {
      let result;

      switch (currentAction) {
        case 'clearEncounter':
          result = await clearEncounterState();
          break;
        case 'clearRecording':
          result = await clearRecordingState();
          break;
        case 'clearAudio':
          result = await clearAudioCache();
          break;
        case 'clearAppState':
          result = await clearReduxPersist(true);
          break;
        case 'clearUIPreferences':
          result = await clearPreferencesAndUI();
          break;
        case 'clearIntegrations':
          result = await clearIntegrations();
          break;
        case 'clearAll':
          result = await clearAllStorage();
          break;
        default:
          return;
      }

      if (result.success) {
        enqueueSnackbar('Storage cleared successfully', { variant: 'success' });

        // Notify parent
        if (onStorageCleared && currentAction) {
          onStorageCleared(currentAction);
        }

        if (result.requiresReload) {
          enqueueSnackbar('Reloading page...', { variant: 'info' });
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        }
      } else {
        enqueueSnackbar(`Failed to clear storage: ${result.error}`, { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('An error occurred while clearing storage', { variant: 'error' });
      console.error('Clear storage error:', error);
    } finally {
      setIsClearing(false);
      setConfirmDialogOpen(false);
      setCurrentAction(null);
    }
  };

  const handleCancelClear = () => {
    setConfirmDialogOpen(false);
    setCurrentAction(null);
  };

  const confirmDialogProps = currentAction ? getConfirmDialogProps(currentAction) : null;

  return (
    <>
      <StyledCard>
        <CardContent>
          <SectionTitle variant="h6">Storage & State Management</SectionTitle>

          {recordingActive && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Storage management is disabled during active recordings
            </Alert>
          )}

          <Grid container spacing={2}>
            {/* Clear Encounter State */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Button
                variant="outlined"
                fullWidth
                disabled={recordingActive}
                onClick={() => handleClearAction('clearEncounter')}
                startIcon={<RefreshIcon />}
              >
                Clear Encounter State
              </Button>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ mt: 0.5, display: 'block' }}
              >
                Fixes: Encounter not loading or stuck on wrong patient
              </Typography>
            </Grid>

            {/* Clear Recording State */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Button
                variant="outlined"
                fullWidth
                disabled={recordingActive}
                onClick={() => handleClearAction('clearRecording')}
                startIcon={<MicOffIcon />}
              >
                Clear Recording State
              </Button>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ mt: 0.5, display: 'block' }}
              >
                Fixes: Recording stuck or frozen
              </Typography>
            </Grid>

            {/* Clear Audio Cache */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => handleClearAction('clearAudio')}
                startIcon={<DeleteIcon />}
              >
                Clear Audio Cache
              </Button>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ mt: 0.5, display: 'block' }}
              >
                Clears cached audio recordings and session data
              </Typography>
            </Grid>

            {/* Reset App State */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Button
                variant="outlined"
                fullWidth
                disabled={recordingActive}
                onClick={() => handleClearAction('clearAppState')}
                startIcon={<SettingsBackupRestoreIcon />}
              >
                Reset App State
              </Button>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ mt: 0.5, display: 'block' }}
              >
                Fixes: App behaving oddly after update (keeps login)
              </Typography>
            </Grid>

            {/* Reset UI Preferences */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => handleClearAction('clearUIPreferences')}
                startIcon={<TuneIcon />}
              >
                Reset UI Preferences
              </Button>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ mt: 0.5, display: 'block' }}
              >
                Fixes: Stuck filters, broken sidebar, missing tooltips
              </Typography>
            </Grid>

            {/* Reset Integrations */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => handleClearAction('clearIntegrations')}
                startIcon={<LinkIcon />}
              >
                Reset Integrations
              </Button>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ mt: 0.5, display: 'block' }}
              >
                Fixes: Google Calendar sync issues, OAuth stuck
              </Typography>
            </Grid>

            {/* Advanced Options */}
            <Grid size={{ xs: 12 }}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Advanced Options</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    <Button
                      variant="text"
                      color="error"
                      onClick={() => handleClearAction('clearAll')}
                      startIcon={<WarningIcon />}
                      fullWidth
                    >
                      Clear All Storage (Logs you out)
                    </Button>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </CardContent>
      </StyledCard>

      {/* Confirmation Dialog */}
      {confirmDialogProps && (
        <StorageClearConfirmDialog
          open={confirmDialogOpen}
          title={confirmDialogProps.title}
          description={confirmDialogProps.description}
          itemsToClear={confirmDialogProps.itemsToClear}
          itemsToKeep={confirmDialogProps.itemsToKeep}
          estimatedSpace={'estimatedSpace' in confirmDialogProps ? (confirmDialogProps.estimatedSpace as string) : undefined}
          requiresReload={confirmDialogProps.requiresReload}
          requiresReauth={confirmDialogProps.requiresReauth}
          isClearing={isClearing}
          onConfirm={handleConfirmClear}
          onCancel={handleCancelClear}
        />
      )}
    </>
  );
};
